import React, {
  useEffect,
  useState,
  FunctionComponent,
  useCallback,
  KeyboardEvent,
} from 'react';
import { Box, Card, Search } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import {
  askingAQuestion,
  clickOnAnswer,
  clickTheSeeAllResultsLink,
} from '@wix/bi-logger-new-help-center/v2';
import {
  useFedops,
  useHttpClient,
  useExperiments,
} from '@wix/fe-essentials-standalone';
import { v4 as uuidv4 } from 'uuid';
import { DATA_HOOKS } from '../../dataHooks';
import { BI, EXPERIMENTS, FEDOPS, PAGES } from '../../constants';
import {
  ArticleSearchResult,
  ArticleSearchResultItem,
  PageType,
} from '../../types';
import { DeprecatedSearchBarResults } from './DeprecatedSearchBarResults';
import { DeprecatedEmptySearchResults } from './DeprecatedEmptySearchResults';
import { useBI } from '../../hooks/useBI';
import {
  mapArticleTypeToItemType,
  mapPageTypeToSourceName,
} from '../../utils/bi';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import css from './index.module.scss';
import { pushUrl } from '../InnerLink';
import { SearchBarResults } from './SearchBarResults';
import { EmptySearchResults } from './EmptySearchResults';

type SearchBarProps = {
  pageType: PageType;
  itemId?: string;
  itemType?: string;
  homepageNewDesign?: boolean;
};

export const SearchBar: FunctionComponent<SearchBarProps> = ({
  pageType,
  itemId,
  itemType,
  homepageNewDesign,
}: SearchBarProps) => {
  const { t } = useTranslation();
  const { locale, asPath } = useRouter();
  const httpClient = useHttpClient();
  const { experiments } = useExperiments({ readOnly: true });
  const [hideResults, setHideResults] = useState(false);
  const { sendBIEvent } = useBI();
  const fedopsApi = useFedops();
  const ref = useOutsideClick(() => {
    setHideResults(true);
  });

  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchSessionId, setSearchSessionId] = useState(uuidv4());
  const [articles, setArticles] = useState<ArticleSearchResultItem[]>([]);
  const [focusedArticleIndex, setFocusedArticleIndex] = useState<number>(-1);

  const showNewSearchBarResults = experiments.enabled(
    EXPERIMENTS.SPECS.NEW_SEARCH_BAR_RESULTS
  );

  const search = useCallback(async (): Promise<void> => {
    if (inputText?.length > 0) {
      setIsLoading(true);
      fedopsApi.interactionStarted(FEDOPS.INTERACTION_NAMES.SEARCH_ARTICLES);
      const { data } = await httpClient.get<ArticleSearchResult>(
        '/api/article/search',
        {
          params: {
            locale,
            text: inputText,
          },
        }
      );
      fedopsApi.interactionEnded(FEDOPS.INTERACTION_NAMES.SEARCH_ARTICLES);

      const newArticles = data.items.slice(0, showNewSearchBarResults ? 6 : 5);
      setFocusedArticleIndex(-1);
      setArticles(newArticles);
      setIsLoading(false);
      await sendBIEvent(
        askingAQuestion({
          source_name: mapPageTypeToSourceName(pageType),
          item_id: itemId,
          item_type: itemType,
          kb_lang: locale as string,
          presented_content: JSON.stringify(
            newArticles.map((article, index) => ({
              order: index + 1,
              item_id: article.id,
              locale,
              type: mapArticleTypeToItemType(article.type),
            }))
          ),
          question: inputText,
          search_session_Id: searchSessionId,
          search_method: data.searchMethod,
          search_version: data.searchVersion,
        })
      );
    } else {
      setArticles([]);
    }
  }, [inputText]);

  const onClear = () => {
    setInputText('');
    setArticles([]);
    setFocusedArticleIndex(-1);
    setSearchSessionId(uuidv4());
  };

  const onSearchResultClick = async (
    { url, type, id }: ArticleSearchResultItem,
    index: number
  ): Promise<void> => {
    await sendBIEvent(
      clickOnAnswer({
        source_name: mapPageTypeToSourceName(pageType),
        item_id: itemId,
        item_type: itemType,
        kb_lang: locale as string,
        clicked_url: url,
        clicked_item_id: id,
        clicked_item_order: `${index + 1}`,
        question: inputText,
        search_session_Id: searchSessionId,
        clicked_item_type: mapArticleTypeToItemType(type),
        search_result_selection:
          BI.SEARCH_RESULT_SELECTION.SEARCH_BAR_DROPDOWN_LIST,
      })
    );
    onClear();
  };

  useEffect(() => {
    onClear();
  }, [asPath]);

  useEffect(() => {
    void (async () => {
      await search();
    })();
  }, [inputText, search]);

  const handleKeyboardEvent = useCallback(
    async (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          setFocusedArticleIndex(
            focusedArticleIndex === articles.length
              ? focusedArticleIndex
              : focusedArticleIndex + 1
          );
          break;
        case 'ArrowUp':
          setFocusedArticleIndex(
            focusedArticleIndex > -1
              ? focusedArticleIndex - 1
              : focusedArticleIndex
          );
          break;
        case 'Enter':
          if (
            focusedArticleIndex >= 0 &&
            focusedArticleIndex <= articles.length - 1
          ) {
            await onSearchResultClick(
              articles[focusedArticleIndex],
              focusedArticleIndex
            );
            await pushUrl(articles[focusedArticleIndex].uri);
          } else if (focusedArticleIndex === articles.length) {
            await pushUrl(`/search?term=${inputText}`);
          }
          break;
      }
    },
    [articles, focusedArticleIndex, inputText]
  );

  const onSearchChange = useCallback((e) => {
    setInputText(e.target.value.trim());
    setFocusedArticleIndex(-1);
  }, []);

  const onManuallyInput = useCallback(
    async (val) => {
      if (focusedArticleIndex === -1) {
        setInputText(val.trim());
      }
    },
    [focusedArticleIndex]
  );

  const onSearchFocus = useCallback(() => {
    setHideResults(false);
    setFocusedArticleIndex(-1);
  }, []);

  const onClickAllResults = async () => {
    await sendBIEvent(
      clickTheSeeAllResultsLink({
        source_name: mapPageTypeToSourceName(pageType),
        item_id: itemId,
        item_type: itemType,
        kb_lang: locale as string,
        question: inputText,
        search_session_Id: searchSessionId,
      })
    );
    onClear();
  };

  return (
    <div
      ref={ref}
      className={css.wrapper}
      data-hook={DATA_HOOKS.SEARCH_BAR}
      onKeyUp={handleKeyboardEvent}
    >
      <Box
        direction="vertical"
        verticalAlign="middle"
        className={`${
          pageType === PAGES.HOMEPAGE
            ? css.searchBarWrapperHomepage
            : css.searchBarWrapperSubHeader
        } ${homepageNewDesign ? css.homepageNewDesign : ''}`}
      >
        <Search
          debounceMs={1000}
          maxLength={100}
          placeholder={t('searchbar.placeholder')}
          onChange={onSearchChange}
          onClear={onClear}
          onManuallyInput={onManuallyInput}
          value={inputText}
          className={css.searchBar}
          dataHook={DATA_HOOKS.SEARCH_BAR_INPUT}
          onFocus={onSearchFocus}
          autoFocus={pageType === PAGES.HOMEPAGE && homepageNewDesign}
        />
        {inputText !== '' && !hideResults && (
          <Card
            showShadow
            className={`${
              pageType === PAGES.HOMEPAGE
                ? css.searchBarResultsWrapperHomepage
                : css.searchBarResultsWrapperSubHeader
            }
                ${showNewSearchBarResults ? css.newSearchBarResults : ''}`}
          >
            {articles.length > 0 &&
              (showNewSearchBarResults ? (
                <SearchBarResults
                  articles={articles}
                  inputText={inputText}
                  focusedArticleIndex={focusedArticleIndex}
                  onClickAllResults={onClickAllResults}
                  onSearchResultClick={onSearchResultClick}
                  setFocusedArticleIndex={setFocusedArticleIndex}
                />
              ) : (
                <DeprecatedSearchBarResults
                  articles={articles}
                  inputText={inputText}
                  itemId={itemId}
                  itemType={itemType}
                  searchSessionId={searchSessionId}
                  pageType={pageType}
                  onClear={onClear}
                  focusedArticleIndex={focusedArticleIndex}
                  setFocusedArticleIndex={setFocusedArticleIndex}
                  onSearchResultClick={onSearchResultClick}
                />
              ))}
            {articles.length === 0 &&
              !isLoading &&
              (showNewSearchBarResults ? (
                <EmptySearchResults inputText={inputText} />
              ) : (
                <DeprecatedEmptySearchResults />
              ))}
          </Card>
        )}
      </Box>
    </div>
  );
};
